import Image from 'next/image';
import Link from 'next/link';
import clsx from 'clsx';
import { COMPANY_DETAILS_QUERYResult } from 'sanity.types';

type CompanyDetailsInfo =
    NonNullable<COMPANY_DETAILS_QUERYResult>[number]['companyDetails'];

interface WhatsAppAndCallButtonsProps {
    fullWidth?: boolean;
    companyDetails: CompanyDetailsInfo | null | undefined;
}

/**
 * The fullWidth param shows the button full width.
 * If it's false, just the logo shows and full width on hover on desktop.
 * On mobile, should always be full width.
 */

const WhatsAppAndCallButtons = ({
    fullWidth = false,
    companyDetails,
}: WhatsAppAndCallButtonsProps) => {
    const commonClasses = [
        'p-2',
        'rounded-full',
        'overflow-hidden',
        'transition-width',
        'duration-500',
        fullWidth ? 'w-[180px]' : 'w-[40px] hover:w-[180px]',
        'h-[40px]',
        'm-[4px]',
    ];

    const phoneButtonClasses = clsx(
        'text-white',
        'md:flex',
        'md:mr-4',
        'bg-[#A3C159]',
        ...commonClasses
    );

    const whatsappButtonClasses = clsx(
        'md:flex',
        'bg-green-100',
        'justify-start',
        ...commonClasses
    );

    const telNumber = companyDetails?.telNumber ?? '';

    // Remove spaces from the telephone number for "Tel" links.
    const noSpacesTelephoneNumber = telNumber?.replace(/ /g, '');

    // Remove spaces and '+' from the telephone number for WhatsApp "wa.me" links.
    const whatsAppNumber = noSpacesTelephoneNumber?.replace(/\+/g, '');

    return (
        <>
            {noSpacesTelephoneNumber ? (
                <Link
                    href={`tel:${noSpacesTelephoneNumber}`}
                    className={`${phoneButtonClasses} md:mb-0`}
                >
                    <Image
                        unoptimized={true}
                        src={'/images/phone_logo.png'}
                        height={56}
                        width={284}
                        alt="Talk to us on the phone"
                        className="max-w-40"
                    />
                </Link>
            ) : null}

            {whatsAppNumber ? (
                <Link
                    href={`https://wa.me/${whatsAppNumber}`}
                    className={whatsappButtonClasses}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Image
                        unoptimized={true}
                        src={'/images/whatsapp_logo.png'}
                        height={56}
                        width={284}
                        alt="Talk to us on Whatsapp"
                        className="max-w-40"
                    />
                </Link>
            ) : null}
        </>
    );
};

export default WhatsAppAndCallButtons;
